<template>
  <cf-validated-form name="giftCardPurchase">
    <Teleport
      defer
      to="#portal-modal-header-target"
    >
      {{ modalTitle }}
    </Teleport>

    <div class="cf-flex cf-flex--column cf-flex--gap-16 cf-padding-y-16">
      <!-- Gift Card Amount Select -->
      <cf-gift-card-amount-select
        v-model="amount"
        :label="`Select an amount ${cardToReload ? 'to add' : 'for your gift card' }`"
        class="cf-text-align-left"
        :amounts="merchant.giftCardConfiguration.amounts.map(({ amount }) => amount)"
        :min-value="merchant.giftCardConfiguration.minimumReloadAmount"
        :max-value="cardToReload ? merchant.giftCardConfiguration.maximumReloadAmount : merchant.giftCardConfiguration.maximumCardAmount"
      />

      <template v-if="merchant.giftCardConfiguration.autoReloadEnabled && (!cardToReload || (!cardToReload.autoReload || !cardToReload.autoReload.enabled))">
        <cf-switch-input
          v-model="autoReloadEnabled"
          name="autoReload"
        >
          <span class="cf-icon cf-icon--heavy far fa-repeat" />

          <span class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
            Auto Reload
          </span>
        </cf-switch-input>

        <transition name="fade-down-only-in">
          <div
            v-if="autoReloadEnabled"
            class="cf-flex cf-flex--column cf-flex--gap-12"
          >
            <p class="cf-subtitle cf-subtitle--2 cf-text-align-left">
              Reload <span class="cf-subtitle--heavy">{{ dollars(amount || 0, { trimZeros: true }) }}</span> when my balance is below
            </p>

            <cf-gift-card-amount-select
              v-model="thresholdTriggerAmount"
              label="Threshold Amount"
              class="cf-text-align-left"
              hide-label
              :amounts="merchant.giftCardConfiguration.thresholdAmounts.map(({ amount }) => amount)"
              :min-value="merchant.giftCardConfiguration.minimumThresholdAmount"
              :max-value="merchant.giftCardConfiguration.maximumThresholdAmount"
            />
          </div>
        </transition>
      </template>

      <!-- Gift Card Design Select -->
      <cf-gift-card-design-select
        v-if="!cardToReload"
        v-model="cardDesignId"
      />

      <cf-payment-methods
        :disabled="!amount"
        pay-cta-text="Pay Now"
        primary-cta-text="Next"
        :payment-modal-title="modalTitle"
        :payment-gateway-override="merchant.defaultPaymentGateway"
        :hidden-elements="{
          contactInfo: 'read-only',
          giftCards: true,
          roomCharge: true,
          signIn: true,
          smsReceipt: true,
          tendr: true,
          total: selectedLocation.features.HidePrices
        }"
        :force-save-cards="autoReloadEnabled"
        :total="amount"
        :checkout-handler="checkout"
      >
        <template #payment-modal-top>
          <!-- Add Funds To Existing Card -->
          <template v-if="cardToReload">
            <cf-expandable-section auto-expand>
              <template #title>
                <h4 class="cf-title cf-title--4">
                  Add Funds
                </h4>
              </template>

              <template #content>
                <ul class="cf-flex cf-flex--column cf-flex--align-start cf-flex--gap-12 cf-bullet-list">
                  <li class="cf-subtitle cf-subtitle--2">
                    Add <span class="cf-subtitle--heavy">{{ dollars(amount, { trimZeros: true }) }}</span> to your card balance now
                  </li>

                  <li class="cf-subtitle cf-subtitle--2">
                    New card balance will be <span class="cf-subtitle--heavy">{{ dollars(cardToReload.balance + amount, { trimZeros: true }) }}</span>
                  </li>
                </ul>
              </template>
            </cf-expandable-section>
          </template>

          <!-- Purchase New Card -->
          <template v-else>
            <cf-expandable-section auto-expand>
              <template #title>
                <h4 class="cf-title cf-title--4">
                  New Gift Card
                </h4>
              </template>

              <template #content>
                <ul class="cf-flex cf-flex--column cf-flex--align-start cf-flex--gap-12 cf-bullet-list">
                  <li class="cf-subtitle cf-subtitle--2">
                    Purchase a new <span class="cf-subtitle--heavy">{{ dollars(amount, { trimZeros: true }) }}</span> gift card
                  </li>
                </ul>
              </template>
            </cf-expandable-section>
          </template>

          <!-- Auto Reload -->
          <template
            v-if="autoReloadEnabled"
          >
            <cf-expandable-section auto-expand>
              <template #title>
                <h4 class="cf-title cf-title--4">
                  Auto Reload Details
                </h4>
              </template>

              <template #content>
                <ul class="cf-flex cf-flex--column cf-flex--align-start cf-flex--gap-12 cf-bullet-list">
                  <li class="cf-subtitle cf-subtitle--2">
                    Reload <span class="cf-subtitle--heavy">{{ dollars(amount, { trimZeros: true }) }}</span>
                    when your card balance is below <span class="cf-subtitle--heavy">{{ dollars(thresholdTriggerAmount, { trimZeros: true }) }}</span>
                  </li>

                  <li class="cf-subtitle cf-subtitle--2">
                    Auto reload stops only when you remove it
                  </li>

                  <li class="cf-subtitle cf-subtitle--2">
                    Will charge the payment method selected below
                  </li>
                </ul>
              </template>
            </cf-expandable-section>
          </template>
        </template>
      </cf-payment-methods>
    </div>
  </cf-validated-form>
</template>


<script>
  import { mapGetters } from 'vuex';

  import errorHandlers from '@/services/error-handlers';

  import dollars from '@/filters/dollars';

  import CfGiftCardDesignSelect from '@/view/components/globals/cf-gift-card-design-select/index.vue';
  import CfGiftCardAmountSelect from '@/view/components/globals/cf-gift-card-amount-select/index.vue';
  import CfPaymentMethods from '@/view/components/globals/cf-payment-methods/index.vue';

  import CfExpandableSection from '@/view/components/shared/cf-expandable-section/index.vue';
  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';
  import CfSwitchInput from '@/view/components/shared/cf-switch-input/index.vue';


  export default {
    name: 'GiftCardPurchase',

    components: {
      CfGiftCardAmountSelect,
      CfGiftCardDesignSelect,
      CfPaymentMethods,
      CfExpandableSection,
      CfValidatedForm,
      CfSwitchInput
    },

    props: {
      cardToReload: {
        type: Object,
        default: null
      },

      overrideReceiptNavigation: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        amount: 0,
        cardDesignId: null,
        autoReloadEnabled: false,
        thresholdTriggerAmount: null
      };
    },

    computed: {
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('session', ['currentUser']),
      ...mapGetters('merchant', ['merchant']),

      modalTitle() {
        return this.cardToReload
          ? 'Add Funds'
          : 'Purchase Gift Card';
      }
    },

    methods: {
      dollars,

      async checkout(options) {
        if (!this.amount || this.amount <= 0) {
          return;
        }

        try {
          const receipt = await this.$store.dispatch('giftCards/purchaseCard', {
            amount: this.amount,
            cardDesignId: this.cardDesignId,
            cardId: this.cardToReload ? this.cardToReload.cardId : null,
            contactInfo: {
              email: this.currentUser.email,
              fullName: this.currentUser.fullName
            },
            paymentGateway: this.merchant.defaultPaymentGateway,
            paymentMethod: options.paymentMethod,
            ...this.autoReloadEnabled && {
              autoReload: {
                enabled: this.autoReloadEnabled,
                thresholdTriggerAmount: this.thresholdTriggerAmount
              }
            }
          });

          options.onCheckoutSuccess();

          this.$closeModal('giftCardDetails');
          this.$closeModal('giftCardPurchase');

          this.$openModal('giftCardReceipt', { receipt, overrideReceiptNavigation: this.overrideReceiptNavigation });
        }

        catch (error) {
          options.onCheckoutError();
          errorHandlers.generic(error);
        }
      }
    }
  };
</script>
